import { TypeIcon } from "../components/common/TypeIcon";
import { usePageBar } from "../contexts/PageBarContext";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useInteractions } from "../hooks/useInteractions";
import { useSegments } from "../hooks/useSegments";
import { useEntityMap } from "../hooks/useEntityMap";
import { useHotelStays } from "../hooks/useHotelStays";
import { fireWrite } from "../utils/offlineWrites";
import { startHotelVisit } from "../utils/activityLogging";
import {
  STAY_ROOM_KEY,
  STAY_ROOM_TYPE_KEY,
  formatStayDay,
  formatStayDuration,
  isCheckOutEvent,
  isStayRunning,
  stayDayBars,
  stayHotel,
  stayHotelName,
  stayNightsLabel,
} from "../utils/hotelStays";
import {
  findCurrentSegmentId,
  getActivityTimeRange,
  isActivityOpen,
  lastLoggedSegmentId,
  segmentDayLookup,
} from "../utils/tripDisplay";
import { formatDayKey } from "../utils/calendarDay";
import type { Trip } from "../types";
import "../components/stays/Stays.css";

const timeOf = (d: Date | null, zone?: string) =>
  d ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: zone }) : "";

/**
 * One hotel stay: when you checked in and out, the room, how long you were
 * actually at the hotel each day, and every visit that made it up. Addressed
 * by the stay's first visit (see `utils/hotelStays`).
 */
export function StayDetail() {
  const { tripId, stayId } = useParams<{ tripId: string; stayId: string }>();
  const [trip, setTrip] = useState<Trip | null>(null);
  const { interactions, loading } = useInteractions(tripId);
  const { segments } = useSegments(tripId);
  const entityIds = useMemo(
    () => [...new Set(interactions.map((i) => i.entityId).filter((id): id is string => !!id))],
    [interactions],
  );
  const { entityDataMap, loading: entitiesLoading } = useEntityMap(entityIds);
  const onDay = useMemo(() => segmentDayLookup(segments), [segments]);
  const { stays } = useHotelStays(interactions, entityDataMap, onDay);
  const stay = stays.find((s) => s.id === stayId) ?? null;
  const zone = trip?.timezone ?? undefined;

  useEffect(() => {
    if (!tripId) return;
    let cancelled = false;
    getDoc(doc(db, "trips", tripId))
      .then((snap) => {
        if (!cancelled && snap.exists()) setTrip(snap.data() as Trip);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  // Room and room type, kept on the stay's first visit.
  const [room, setRoom] = useState("");
  const [roomType, setRoomType] = useState("");
  useEffect(() => {
    setRoom(stay?.room ?? "");
    setRoomType(stay?.roomType ?? "");
  }, [stay?.room, stay?.roomType]);
  const saveDetail = (key: string, value: string) => {
    if (!stay) return;
    fireWrite(
      updateDoc(doc(db, "interactions", stay.id), {
        [`customResponses.${key}`]: value.trim() || null,
        updatedAt: serverTimestamp(),
      }),
      "save stay details",
    );
  };

  const bars = useMemo(() => (stay ? stayDayBars(stay, zone) : []), [stay, zone]);

  // Add visit: back at the hotel, Arrived now, filed where Today would file it.
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const handleAddVisit = async () => {
    if (!user || !stay || !tripId || adding) return;
    setAdding(true);
    try {
      const id = await startHotelVisit({
        userId: user.uid,
        tripId,
        segmentId: findCurrentSegmentId(segments, lastLoggedSegmentId(interactions)),
        hotel: stay,
      });
      navigate(`/trips/${tripId}/interactions/${id}`);
    } catch {
      setAdding(false);
    }
  };

  usePageBar({
    title: stay ? stayHotelName(stay, stayHotel(stay, entityDataMap)) : null,
    parent: { to: `/trips/${tripId}`, label: trip?.name ?? "Trip" },
  });

  if (loading || entitiesLoading) return <div className="loading">Loading stay…</div>;
  if (!stay || !tripId) {
    return (
      <div className="page">
        <p>
          Stay not found. <Link to={`/trips/${tripId ?? ""}`}>Back to trip</Link>
        </p>
      </div>
    );
  }

  const hotel = stayHotel(stay, entityDataMap);
  const hotelName = stayHotelName(stay, hotel);
  const running = isStayRunning(stay);
  const lastVisit = stay.visits[stay.visits.length - 1];
  const atHotel = !!lastVisit?.events?.length && isActivityOpen(lastVisit.events);
  // Only mid-stay and while you're out: a visit starts now, so an old stay
  // or one you're still in has nothing to add.
  const canAddVisit = running && !stay.checkedOut && !atHotel;

  return (
    <div className="page stay-page">
      <div className="stay-head">
        <span className="stay-card-icon stay-head-icon" aria-hidden="true">
          {hotel?.imageUrl ? <img src={hotel.imageUrl} alt="" /> : <TypeIcon type="HOTEL" tile />}
        </span>
        <div>
          <h1 className="stay-head-name">
            {stay.entityId ? <Link to={`/entities/${stay.entityId}`}>{hotelName}</Link> : hotelName}
          </h1>
          <div className="stay-head-sub">Stay · {stayNightsLabel(stay, zone)}</div>
        </div>
      </div>

      <div className="stay-facts">
        <div className="stay-fact">
          <div className="stay-fact-k">Check in</div>
          <div className="stay-fact-v">{formatStayDay(stay.checkIn, zone)}</div>
          <div className="stay-fact-t">{timeOf(stay.checkIn, zone)}</div>
        </div>
        <div className="stay-fact">
          <div className="stay-fact-k">Check out</div>
          {stay.checkOut ? (
            <>
              <div className="stay-fact-v">{formatStayDay(stay.checkOut, zone)}</div>
              <div className="stay-fact-t">{timeOf(stay.checkOut, zone)}</div>
            </>
          ) : (
            <div className="stay-fact-v stay-fact-muted">{running ? "Still staying" : "Not logged"}</div>
          )}
        </div>
        <label className="stay-fact">
          <span className="stay-fact-k">Room</span>
          <input
            className="stay-fact-input"
            value={room}
            placeholder="Add"
            onChange={(e) => setRoom(e.target.value)}
            onBlur={() => room.trim() !== (stay.room ?? "") && saveDetail(STAY_ROOM_KEY, room)}
          />
        </label>
        <label className="stay-fact">
          <span className="stay-fact-k">Room type</span>
          <input
            className="stay-fact-input"
            value={roomType}
            placeholder="View, beds…"
            onChange={(e) => setRoomType(e.target.value)}
            onBlur={() => roomType.trim() !== (stay.roomType ?? "") && saveDetail(STAY_ROOM_TYPE_KEY, roomType)}
          />
        </label>
      </div>

      {bars.length > 0 && (
        <section>
          <div className="stay-section-head">
            <h2>Time at the hotel</h2>
            <span className="stay-total">{formatStayDuration(stay.timeAtHotelMs)}</span>
          </div>
          <div className="stay-bars" role="img" aria-label={`At the hotel ${formatStayDuration(stay.timeAtHotelMs)} over ${bars.length} days`}>
            {bars.map((bar) => (
              <div key={bar.dayKey} className="stay-bar-row">
                <span className="stay-bar-day">{formatDayKey(bar.dayKey, { weekday: "short" })}</span>
                <span className="stay-bar-track">
                  {bar.spans.map((s, i) => (
                    <span
                      key={i}
                      className="stay-bar-span"
                      style={{ left: `${s.from * 100}%`, width: `${Math.max(0.6, (s.to - s.from) * 100)}%` }}
                    />
                  ))}
                </span>
              </div>
            ))}
            <div className="stay-bar-row stay-bar-axis" aria-hidden="true">
              <span className="stay-bar-day" />
              <span className="stay-bar-ticks">
                <span>12a</span>
                <span>6a</span>
                <span>12p</span>
                <span>6p</span>
                <span>12a</span>
              </span>
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="stay-section-head">
          <h2>Visits</h2>
          {canAddVisit && (
            <button type="button" className="btn stay-add-visit" onClick={handleAddVisit} disabled={adding}>
              ＋ Add visit
            </button>
          )}
        </div>
        <div className="stay-visits">
          {stay.visits.map((visit, i) => {
            const range = getActivityTimeRange(visit.events);
            const open = (visit.events?.length ?? 0) > 0 && isActivityOpen(visit.events);
            const steps = [...(visit.events ?? [])]
              .sort((a, b) => a.eventTimestamp.toDate().getTime() - b.eventTimestamp.toDate().getTime())
              .map((e) => e.customLabel)
              .filter((l): l is string => !!l && l !== "Arrived" && l !== "Left");
            const tags = [
              i === 0 ? "Check-in" : null,
              (visit.events ?? []).some(isCheckOutEvent) ? "Check-out" : null,
            ].filter(Boolean);
            const inside = interactions.filter((x) => x.parentInteractionId === visit.id).length;
            return (
              <Link key={visit.id} to={`/trips/${tripId}/interactions/${visit.id}`} className="stay-visit">
                <span className="stay-visit-text">
                  <span className="stay-visit-name">
                    {formatStayDay(range.start, zone)}
                    {tags.length > 0 ? ` · ${tags.join(" · ")}` : ""}
                  </span>
                  {(steps.length > 0 || inside > 0) && (
                    <span className="stay-visit-sub">
                      {[steps.join(", "), inside > 0 ? `${inside} inside` : ""].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </span>
                <span className="stay-visit-times">
                  <span>{timeOf(range.start, zone)}</span>
                  <span>{open ? "now" : timeOf(range.end, zone)}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
